import React, { useState } from 'react';
import { Tooltip } from '@mantine/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { icon } from '@fortawesome/fontawesome-svg-core/import.macro';
import ProposalRichTextBubbleEditor from './ProposalRichTextBubbleEditor';
import richTextStyles from './ProposalRichText.module.css';
import { isRichTextHtml } from '../../../utils/richTextHtml';
import { sanitizeRichHtml } from '../../../utils/sanitizeRichHtml';
import { withTextBackgroundStyle } from '../../../utils/textBackgroundStyle';
import ProposalBoxAlign from '../ProposalBoxAlign/ProposalBoxAlign';

/**
 * Texte riche éditable sur le canvas Puck (bubble menu Tiptap) ou rendu HTML en lecture.
 *
 * @param {string} id - id Puck du bloc parent
 * @param {object} [puck]
 * @param {string} [html]
 * @param {string} [contentProp] - prop racine (ex. `text`, `subtitle`)
 * @param {string} [contentPath] - chemin imbriqué setDeep (ex. `slides[0].subtitle`)
 * @param {string} [className]
 * @param {object} [style]
 * @param {number} [minHeight]
 * @param {string} [placeholder]
 * @param {object} [textStyle]
 * @param {string} [backgroundColor] - fond zone (CSS, vide = transparent)
 */
export default function PuckInlineRichText({
  id,
  puck,
  html = '',
  contentProp = 'html',
  contentPath = null,
  className = '',
  style,
  minHeight,
  placeholder = '',
  textStyle = null,
  drawMode = false,
  alignHorizontal = 'left',
  alignVertical = 'center',
  backgroundColor = '',
  reducedToolbar = false,
  /** Puce « espace réservé » quand le contenu est vide. Absente en drawMode
   *  (box positionnée : la puce fausserait le cadrage), sauf demande
   *  explicite — un libellé d'onglet vide doit se voir comme partout ailleurs. */
  showPlaceholderChip = !drawMode,
  /** 'fixed' : le bubble menu se positionne par rapport au viewport et
   *  s'affranchit du rognage d'un ancêtre `overflow` (barre d'onglets). */
  bubbleStrategy,
  focusAt,
  onFocusApplied,
}) {
  const isEditing = !!puck?.isEditing;
  const [focused, setFocused] = useState(false);
  const resolvedBackground = backgroundColor || textStyle?.backgroundColor || '';
  const viewStyle = withTextBackgroundStyle(style, resolvedBackground);

  if (isEditing && id) {
    const editor = (
      <ProposalRichTextBubbleEditor
        id={id}
        html={html}
        contentProp={contentProp}
        contentPath={contentPath}
        className={className}
        style={style}
        minHeight={minHeight}
        textStyle={textStyle}
        drawMode={drawMode}
        backgroundColor={resolvedBackground}
        reducedToolbar={reducedToolbar}
        placeholder={placeholder}
        bubbleStrategy={bubbleStrategy}
        focusAt={focusAt}
        onFocusApplied={onFocusApplied}
      />
    );

    // drawMode : box positionnée en absolu, pas d'enveloppe d'alignement.
    const aligned = drawMode ? editor : (
      <ProposalBoxAlign horizontal={alignHorizontal} vertical={alignVertical} innerMode="full">
        {editor}
      </ProposalBoxAlign>
    );

    // Sans placeholder, ou puce refusée : rendu classique.
    if (!placeholder || !showPlaceholderChip) return aligned;

    // Avec placeholder : quand le contenu est vide ET pas encore focus, on
    // affiche une puce pointillée + œil barré (espace réservé, non rendu sur le
    // site tant que vide). Dès le focus → édition classique. Structure STABLE
    // (Tooltip + div toujours présents, bascule via style/disabled) : sinon
    // remonter l'éditeur perdrait le focus en pleine frappe. Tooltip Mantine
    // (instantané) au lieu de l'attribut title (délai navigateur).
    const isEmpty = !String(html || '').replace(/<[^>]+>/g, '').trim();
    const showChip = isEmpty && !focused;
    return (
      <Tooltip
        label="Espace réservé — non affiché sur le site tant qu'il est vide."
        withArrow
        openDelay={0}
        disabled={!showChip}
      >
        <div
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          style={showChip ? {
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            border: '1px dashed var(--mantine-color-gray-4)',
            borderRadius: 6,
            padding: '1px 8px',
            color: 'var(--mantine-color-dimmed)',
          } : { display: 'contents' }}
        >
          <FontAwesomeIcon icon={icon({ name: 'eye-slash', style: 'light' })} style={{ fontSize: 14, flexShrink: 0, display: showChip ? 'inline-flex' : 'none' }} />
          {aligned}
        </div>
      </Tooltip>
    );
  }

  if (!html) {
    return null;
  }

  if (isRichTextHtml(html)) {
    return (
      <div
        className={`${richTextStyles.richText} ${className}`.trim()}
        style={viewStyle}
        dangerouslySetInnerHTML={{ __html: sanitizeRichHtml(html) }}
      />
    );
  }

  return (
    <span className={className} style={viewStyle}>{html}</span>
  );
}
